import { Slide } from '@features/powerpoint';
import { BuildReportOptions } from '../../utils/build-presentation';
import { dimensionSlide } from '@features/executive-report/slides/v2/dimension';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { navbarSections } from '@features/executive-report/subdomains/visitation/domain/navbar-sections';
import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { formatters } from '@utils/formattersV2';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import { visibleSlide } from '@features/executive-report/slides';
import { FormSchema } from '../../form-schema';
import { topSignificantDimensions } from '../../domain/significant-dimensions';
import { BuildReportOptionsV2 } from '../../utils/build-slides-v2';

type BuildDimensionSlideProps = {
  navbarSection: (typeof navbarSections)[number];
  dimensionName: string;
  dimensionSlug: string;
  headerPrefix: string;
  dimensions: BuildReportOptions['report']['dimensions'];
  overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
};

const buildDimensionSlide = (props: BuildDimensionSlideProps): Slide => {
  if (props.dimensions.length === 0) return {};

  return dimensionSlide({
    navbarText: buildNavbarText(navbarSections, props.navbarSection),
    dimensionName: props.dimensionName,
    overallMetrics: props.overallMetrics,
    dimensionMetrics: props.dimensions.map(
      (
        subdimension
      ): Parameters<typeof dimensionSlide>[0]['dimensionMetrics'][number] => ({
        dimension: subdimension.dimensionValue,
        image:
          props.subdimensionImages[props.dimensionSlug]?.[
            subdimension.dimensionValue
          ] ?? null,
        metrics: [
          {
            label: 'Sales Lift',
            value: formatWithSign(
              subdimension.incrementalSalesLift,
              formatters.lift
            ),
          },
          {
            label: 'Incremental Sales',
            value: formatters.currency(subdimension.projectedIncrementalSales, {
              full: true,
              decimals: 0,
            }),
          },
          {
            label: 'Impressions',
            value: formatters.impressions(subdimension.impressions, {
              full: true,
            }),
          },
        ],
      })
    ),
    headerText: {
      text: [
        {
          text: `${props.headerPrefix}: `,
          options: {
            color: themeV2.colors.inmarketGreen,
          },
        },
        {
          text: `${props.dimensionName}s`,
        },
      ],
      options: {
        fontSize: 36,
        bold: true,
        h: 0.75,
        w: 12.67,
        x: 0.33,
        y: 0.59,
      },
    },
  });
};

export const salesDimensionsSlides = (props: BuildReportOptionsV2): Slide[] => {
  const overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'] =
    [
      {
        label: 'Incremental Sales and Lift',
        value: formatters.currency(
          props.report.projectedIncrementalSales ?? 0,
          { decimals: 1 }
        ),
        lift: formatWithSign(
          props.report.incrementalSalesLift ?? 0,
          formatters.lift
        ),
      },
      {
        label: 'Transaction Size and Lift',
        value: formatters.currency(props.report.transactionSize ?? 0, {
          decimals: 0,
        }),
        lift: formatWithSign(
          props.report.transactionSizeLift ?? 0,
          formatters.lift
        ),
      },
      {
        label: 'Penetration and Lift',
        value: formatters.percentage(props.report.penetration ?? 0, {
          decimals: 1,
        }),
        lift: formatWithSign(
          props.report.penetrationLift ?? 0,
          formatters.lift
        ),
      },
      {
        label: 'Transaction Rate and Lift',
        value: formatters.number(props.report.transactionRate ?? 0, {
          decimals: 1,
        }),
        lift: formatWithSign(
          props.report.transactionRateLift ?? 0,
          formatters.lift
        ),
      },
    ];

  const audienceDimensions = topSignificantDimensions(
    props.report.dimensions,
    'aud'
  );
  const creativeDimensions = topSignificantDimensions(
    props.report.dimensions,
    'creative'
  );
  const channelDimensions = topSignificantDimensions(
    props.report.dimensions,
    'channel'
  );

  return [
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Who',
        dimensionName: 'Audience',
        dimensionSlug: 'aud',
        headerPrefix: 'The WHO',
        dimensions: audienceDimensions,
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.salesLift.who &&
        audienceDimensions.length > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The What',
        dimensionName: 'Creative',
        dimensionSlug: 'creative',
        headerPrefix: 'The WHAT',
        dimensions: creativeDimensions,
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.salesLift.what &&
        creativeDimensions.length > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Where',
        dimensionName: 'Channel',
        dimensionSlug: 'channel',
        headerPrefix: 'The WHERE',
        dimensions: channelDimensions,
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
      }),
      visible:
        props.customFields.includedSlides.salesLift.where &&
        channelDimensions.length > 0,
    }),
  ];
};
